import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PageHeader } from '../../components/partials';
import { Spinner, WarningMessage } from '../../components/widgets';
import GeneralPopup, { PopupTypes } from '../../components/organisms/GeneralPopup';
import { getNavigationSections } from '../../selectors/navigation';
import IntegrationTable from './IntegrationTable';
import css from './IntegrationHomepage.css';
import IntegrationRequestService from '../service';
import { PENDING_REVIEW_STATUSES, REQUEST_TYPE_LABELS, TRANSLATE_PREFIX } from '../consts';
import { trackRequestDeleted, trackRequestSubmitted } from '../mixpanel';
import { isPartnerInfoMissing, isFormType } from '../utils';

const service = new IntegrationRequestService();

const HELP_CENTER_LINK = 'https://support.singular.net/hc/en-us/categories/360000141451';

const IntegrationHomepage = ({
    requests,
    loading,
    loaded,
    error: initialError = null,
    onAddRequest,
    onUpdateRequest,
    translate,
    hiddenByType = {},
}) => {
    const location = useLocation();
    const navigationSections = useSelector(getNavigationSections);
    const profileIncomplete = isPartnerInfoMissing(navigationSections);
    const [error, setError] = useState(initialError);
    const [pendingDeleteRequest, setPendingDeleteRequest] = useState(null);
    const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);
    const hasPendingReviewRequests = requests.some(r => PENDING_REVIEW_STATUSES.includes(r.status));

    const handleRequestIntegration = card => {
        setError(null);
        setSuccessMessage(null);
        return service.createIntegrationRequest({ requestType: card.type }).then(newRequest => {
            onAddRequest(newRequest);
            // Non-form types are submitted immediately on creation (no form flow)
            trackRequestSubmitted({ requestType: card.type });
            const messageKey = isFormType(card.type)
                ? `${TRANSLATE_PREFIX}.MESSAGES.DRAFT_CREATED`
                : `${TRANSLATE_PREFIX}.MESSAGES.SUBMITTED`;
            setSuccessMessage({ key: messageKey, typeKey: card.titleKey });
        }).catch(() => {
            setError(`${TRANSLATE_PREFIX}.ERRORS.CREATE_REQUEST`);
        });
    };

    const handleDeleteClick = (e, request) => {
        e.stopPropagation();
        setPendingDeleteRequest(request);
    };

    const handleDeleteConfirm = () => {
        const request = pendingDeleteRequest;
        const typeLabel = REQUEST_TYPE_LABELS[request.request_type] || '';
        setPendingDeleteRequest(null);
        setError(null);
        setSuccessMessage(null);
        service
            .archiveIntegrationRequest(request.id)
            .then(archivedRequest => {
                onUpdateRequest(archivedRequest);
                trackRequestDeleted({
                    requestType: request.request_type,
                    status: request.status,
                });
                setSuccessMessage({ key: `${TRANSLATE_PREFIX}.MESSAGES.DRAFT_DELETED`, typeKey: typeLabel });
            })
            .catch(() => {
                setError(`${TRANSLATE_PREFIX}.ERRORS.DELETE_REQUEST`);
            });
    };

    if (loading && !loaded) {
        return (
            <div className={css.container}>
                <div className={css.loading}>
                    <Spinner show />
                </div>
            </div>
        );
    }

    return (
        <div className={css.container}>
            <PageHeader
                text={`${TRANSLATE_PREFIX}.PAGE_HEADER`}
                shouldAddPadding
                className={css.pageHeader}
                subText={{ faqLink: HELP_CENTER_LINK, tourId: null, subTitle: `${TRANSLATE_PREFIX}.PAGE_DESCRIPTION` }}
                helpMenuContainerClass={css.helpMenuContainer}
            />
            {hasPendingReviewRequests && (
                <WarningMessage
                    type="info"
                    showIcon={false}
                    message={<Translate id={`${TRANSLATE_PREFIX}.MESSAGES.TICKET_SUBMITTED`} />}
                    className={css.banner}
                />
            )}
            {error && <WarningMessage type="error" message={<Translate id={error} />} className={css.banner} />}
            {successMessage && (
                <WarningMessage
                    type="success"
                    message={
                        <Translate
                            id={successMessage.key}
                            data={{ type: successMessage.typeKey ? translate(successMessage.typeKey) : '' }}
                        />
                    }
                    className={css.banner}
                />
            )}
            {profileIncomplete && (
                <WarningMessage
                    type="warning"
                    message={<Translate id={`${TRANSLATE_PREFIX}.ERRORS.PROFILE_INCOMPLETE`} />}
                    className={css.banner}
                />
            )}
            <div className={css.tableContainer}>
                {loading && loaded && (
                    <div className={css.loading}>
                        <Spinner show />
                    </div>
                )}
                <IntegrationTable
                    requests={requests}
                    hiddenByType={hiddenByType}
                    profileIncomplete={profileIncomplete}
                    onRequestIntegration={handleRequestIntegration}
                    onDeleteClick={handleDeleteClick}
                />
            </div>
            <GeneralPopup
                open={!!pendingDeleteRequest}
                type={PopupTypes.DELETE}
                title={`${TRANSLATE_PREFIX}.DELETE_CONFIRM.TITLE`}
                text={`${TRANSLATE_PREFIX}.DELETE_CONFIRM.TEXT`}
                acceptText={`${TRANSLATE_PREFIX}.DELETE_CONFIRM.ACCEPT`}
                rejectText={`${TRANSLATE_PREFIX}.DELETE_CONFIRM.REJECT`}
                onAccept={handleDeleteConfirm}
                onReject={() => setPendingDeleteRequest(null)}
            />
        </div>
    );
};

IntegrationHomepage.propTypes = {
    requests: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            request_type: PropTypes.string,
            status: PropTypes.string,
            updated_at: PropTypes.string,
        })
    ).isRequired,
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    error: PropTypes.string,
    hiddenByType: PropTypes.objectOf(PropTypes.array),
    onAddRequest: PropTypes.func.isRequired,
    onUpdateRequest: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(IntegrationHomepage);
